/**
 * React Query hooks for Tier-2 vendor classification rules — the "given this
 * vendor, pre-fill these fields" defaults surfaced on the bill importer's rules
 * page (docs/platform/learned-match-rules-design.md § 5c).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vendorRulesApi } from '../services/accountingApi';
import type { VendorRuleUpdateRequest } from '../services/accountingApi';

const VENDOR_RULES_KEY = 'accounting-vendor-rules';

export function useVendorRules(params?: { is_active?: boolean }) {
  return useQuery({
    queryKey: [VENDOR_RULES_KEY, params],
    queryFn: () => vendorRulesApi.list(params),
  });
}

export function useUpdateVendorRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: VendorRuleUpdateRequest }) =>
      vendorRulesApi.update(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VENDOR_RULES_KEY] });
    },
  });
}

export function useDeleteVendorRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => vendorRulesApi.delete(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VENDOR_RULES_KEY] });
    },
  });
}
